import { useState } from 'react'
import { Sun, Moon, Monitor, ChevronDown } from 'lucide-react'
import { useSettings } from '../hooks/useSettings'
import { syncThemeToStorage } from '../hooks/useThemeEffect'
import { cn } from '../lib/utils'

interface ThemeToggleProps {
  className?: string
}

type Theme = 'light' | 'dark' | 'system'

const themes: { value: Theme; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

function applyTheme(theme: Theme) {
  syncThemeToStorage(theme)
  const resolved = theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme
  document.documentElement.classList.remove('light', 'dark')
  document.documentElement.classList.add(resolved)
}

// Full theme toggle with labels - for settings page
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { settings, updateSetting } = useSettings()
  const currentTheme = (settings?.theme || 'dark') as Theme

  async function handleThemeChange(theme: Theme) {
    applyTheme(theme)
    try {
      await updateSetting('theme', theme)
    } catch (err) {
      console.error('Failed to update theme:', err)
    }
  }

  return (
    <div className={cn('flex gap-1', className)}>
      {themes.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          onClick={() => handleThemeChange(value)}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
            currentTheme === value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
          )}
        >
          <Icon className="w-4 h-4" />
          <span>{label}</span>
        </button>
      ))}
    </div>
  )
}

// Compact dropdown toggle - for sidebar
export function ThemeToggleCompact({ className }: ThemeToggleProps) {
  const { settings, updateSetting } = useSettings()
  const [isOpen, setIsOpen] = useState(false)
  const currentTheme = (settings?.theme || 'dark') as Theme
  const currentConfig = themes.find(t => t.value === currentTheme) || themes[1]
  const CurrentIcon = currentConfig.icon

  async function handleThemeChange(theme: Theme) {
    applyTheme(theme)
    setIsOpen(false)
    try {
      await updateSetting('theme', theme)
    } catch (err) {
      console.error('Failed to update theme:', err)
    }
  }

  return (
    <div className={cn('relative', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
      >
        <CurrentIcon className="w-4 h-4" />
        <span className="flex-1 text-left">{currentConfig.label}</span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 right-0 bottom-full mb-1 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
            {themes.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => handleThemeChange(value)}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors',
                  currentTheme === value
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                )}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
                {currentTheme === value && <span className="ml-auto">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Icon-only toggle - cycles through themes on click
export function ThemeToggleIcon({ className }: ThemeToggleProps) {
  const { settings, updateSetting } = useSettings()
  const currentTheme = (settings?.theme || 'dark') as Theme
  const currentIndex = themes.findIndex(t => t.value === currentTheme)
  const CurrentIcon = themes[currentIndex >= 0 ? currentIndex : 1].icon

  async function cycleTheme() {
    const nextIndex = (currentIndex + 1) % themes.length
    const nextTheme = themes[nextIndex].value
    applyTheme(nextTheme)
    try {
      await updateSetting('theme', nextTheme)
    } catch (err) {
      console.error('Failed to update theme:', err)
    }
  }

  return (
    <button
      onClick={cycleTheme}
      className={cn(
        'p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors',
        className
      )}
      title={`Theme: ${themes[currentIndex >= 0 ? currentIndex : 1].label} (click to change)`}
    >
      <CurrentIcon className="w-4 h-4" />
    </button>
  )
}
